// =============================================================================
// Zakładka Kolejka — monitor `print_jobs`. Dane na żywo przez Supabase Realtime
// (D10) Z OBOWIĄZKOWYM fallbackiem poll 15 s (`usePrintJobs` — dopóki tabela
// nie wejdzie do publikacji `supabase_realtime`, kanał milczy i bez fallbacku
// zakładka byłaby martwa; użytkownik widzi wtedy uczciwy pasek „odświeżanie co
// 15 s", nie udawane „na żywo").
//
// Anulowanie WYŁĄCZNIE dla `queued` — zadania przejętego przez agenta nie da
// się przerwać (papier może już istnieć), więc przycisku po prostu nie ma.
// Ponów = istniejący reprint print-core (klonuje bajty, nigdy re-render).
// =============================================================================

import type { PrintJobStatus } from '@insolitum/types';
import {
  Alert,
  Badge,
  Button,
  Select,
  StatsCard,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableLoading,
  TableRow,
} from '@insolitum/ui';
import { ListTodo, RefreshCw, X } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../providers/AuthProvider';

import {
  KOMUNIKAT_BRAK_PRINT_CORE,
  PrintCoreError,
  anulujZadanie,
  ponowZadanie,
  printCoreDostepny,
} from './printCoreApi';
import { STATUS_ZADANIA, formatujCzasWzgledny, formatujDate, opiszZadanie } from './status';
import {
  useAgents,
  usePrinters,
  usePrintJobs,
  type FiltryZadan,
  type ZadanieWiersz,
} from './usePrintingData';

const ZAKRESY: Array<{ wartosc: string; etykieta: string; ms: number | null }> = [
  { wartosc: '1h', etykieta: 'Ostatnia godzina', ms: 3600_000 },
  { wartosc: '24h', etykieta: 'Ostatnie 24 h', ms: 24 * 3600_000 },
  { wartosc: '7d', etykieta: 'Ostatnie 7 dni', ms: 7 * 24 * 3600_000 },
  { wartosc: '30d', etykieta: 'Ostatnie 30 dni', ms: 30 * 24 * 3600_000 },
  { wartosc: '', etykieta: 'Cała historia', ms: null },
];

interface Props {
  /** Filtr ?site= — zadania dostaną `site_id` w Fazie 7 (migracja M7). */
  siteId: string | null;
}

export function JobsTab(_props: Props) {
  const { t } = useTranslation();
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.organization_id;
  const zapisMozliwy = printCoreDostepny();
  const [searchParams, setSearchParams] = useSearchParams();

  const [status, setStatus] = useState<PrintJobStatus | ''>('');
  const [dokument, setDokument] = useState('');
  const [drukarka, setDrukarka] = useState('');
  const [zakres, setZakres] = useState('24h');

  const odIso = useMemo(() => {
    const wybor = ZAKRESY.find((z) => z.wartosc === zakres);
    return wybor?.ms ? new Date(Date.now() - wybor.ms).toISOString() : '';
    // Zakres wystarczy przeliczać przy zmianie wyboru — dryf „teraz" i tak
    // nadrabia odświeżanie listy (Realtime/poll).
  }, [zakres]);

  const filtry: FiltryZadan = useMemo(
    () => ({ status, dokument, drukarka, odIso }),
    [status, dokument, drukarka, odIso]
  );

  const { zadania, statystyki, naZywo, loading, blad, odswiez } = usePrintJobs(filtry);
  const { drukarki } = usePrinters();
  const { agenci } = useAgents();

  const [wybrane, setWybrane] = useState<ZadanieWiersz | null>(null);
  const [bladAkcji, setBladAkcji] = useState<string | null>(null);
  const [sukces, setSukces] = useState<ReactNode | null>(null);

  const nazwaDrukarki = useMemo(() => {
    const mapa = new Map<string, string>();
    for (const d of drukarki) mapa.set(d.id, d.name);
    return (id: string | null) => (id ? (mapa.get(id) ?? id.slice(0, 8)) : '—');
  }, [drukarki]);

  const nazwaSerwera = useMemo(() => {
    const mapa = new Map<string, string>();
    for (const a of agenci) mapa.set(a.id, a.name);
    return (id: string | null) => (id ? (mapa.get(id) ?? id.slice(0, 8)) : '—');
  }, [agenci]);

  // Głębokie linkowanie ?job= (np. z „Wydruk testowy" na kartotece): wiersz
  // czytamy wprost z bazy przez RLS — lista mogła go już wypchnąć poza limit.
  useEffect(() => {
    const jobId = searchParams.get('job');
    if (!jobId || !orgId) return;
    void supabase
      .from('print_jobs')
      .select(
        'id,document_type,format,status,attempts,error,printer_id,target_agent_id,template_id,' +
          'copies,created_by,claimed_by,created_at,picked_at,printed_at,done_at,slad'
      )
      .eq('organization_id', orgId)
      .eq('id', jobId)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setWybrane(data as unknown as ZadanieWiersz);
      });
  }, [searchParams, orgId]);

  const zamknijDrawer = () => {
    setWybrane(null);
    if (searchParams.get('job')) {
      const nastepne = new URLSearchParams(searchParams);
      nastepne.delete('job');
      setSearchParams(nastepne, { replace: true });
    }
  };

  const ponow = async (zadanie: ZadanieWiersz) => {
    if (!orgId) return;
    if (
      zadanie.status === 'done' &&
      !confirm(
        'Zadanie zakończyło się sukcesem — ponowienie wydrukuje ten sam dokument jeszcze raz.'
      )
    ) {
      return;
    }
    setBladAkcji(null);
    try {
      const wynik = await ponowZadanie(orgId, zadanie.id);
      setSukces(
        t('printing.reprintDone', 'Ponowiono — nowe zadanie {{id}} czeka w kolejce.', {
          id: wynik.job.id.slice(0, 8),
        })
      );
      zamknijDrawer();
      void odswiez();
    } catch (err) {
      setBladAkcji(err instanceof PrintCoreError ? err.message : 'Ponowienie nie powiodło się');
    }
  };

  const anuluj = async (zadanie: ZadanieWiersz) => {
    if (!orgId) return;
    setBladAkcji(null);
    try {
      await anulujZadanie(orgId, zadanie.id);
      setSukces(t('printing.cancelDone', 'Zadanie anulowane.'));
      zamknijDrawer();
      void odswiez();
    } catch (err) {
      setBladAkcji(err instanceof PrintCoreError ? err.message : 'Anulowanie nie powiodło się');
    }
  };

  const typyDokumentow = useMemo(() => {
    const zbior = new Set(zadania.map((z) => z.document_type));
    if (dokument) zbior.add(dokument);
    return [...zbior].sort();
  }, [zadania, dokument]);

  const kolumn = 6;

  return (
    <div className="space-y-4">
      {/* Pasek trybu: mówimy wprost, czy Realtime dostarcza, czy jedziemy pollem. */}
      {!naZywo && (
        <Alert variant="info">
          {t(
            'printing.pollFallback',
            'Podgląd na żywo niedostępny — lista odświeża się co 15 sekund.'
          )}
        </Alert>
      )}
      {blad && (
        <Alert variant="error">
          {blad}{' '}
          <button onClick={() => void odswiez()} className="underline">
            {t('printing.retry', 'Spróbuj ponownie')}
          </button>
        </Alert>
      )}
      {bladAkcji && (
        <Alert variant="error" dismissible onDismiss={() => setBladAkcji(null)}>
          {bladAkcji}
        </Alert>
      )}
      {sukces && (
        <Alert variant="success" dismissible onDismiss={() => setSukces(null)}>
          {sukces}
        </Alert>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard title={t('printing.statQueued', 'W kolejce')} value={statystyki.wKolejce} />
        <StatsCard
          title={t('printing.statPrinting', 'Drukuje')}
          value={statystyki.drukuje}
          accentColor="info"
        />
        <StatsCard
          title={t('printing.statToday', 'Wydrukowane dziś')}
          value={statystyki.dzisiaj}
          accentColor="success"
        />
        <StatsCard
          title={t('printing.statErrors', 'Błędy 24 h')}
          value={statystyki.bledy24h}
          accentColor="danger"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="w-44">
          <Select
            aria-label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as PrintJobStatus | '')}
            options={[
              { value: '', label: t('printing.allStatuses', 'Wszystkie statusy') },
              ...Object.entries(STATUS_ZADANIA).map(([wartosc, opis]) => ({
                value: wartosc,
                label: opis.etykieta,
              })),
            ]}
          />
        </div>
        <div className="w-52">
          <Select
            aria-label="Typ dokumentu"
            value={dokument}
            onChange={(e) => setDokument(e.target.value)}
            options={[
              { value: '', label: t('printing.allDocs', 'Wszystkie dokumenty') },
              ...typyDokumentow.map((typ) => ({ value: typ, label: typ })),
            ]}
          />
        </div>
        <div className="w-52">
          <Select
            aria-label="Drukarka"
            value={drukarka}
            onChange={(e) => setDrukarka(e.target.value)}
            options={[
              { value: '', label: t('printing.allPrinters', 'Wszystkie drukarki') },
              ...drukarki.map((d) => ({ value: d.id, label: d.name })),
            ]}
          />
        </div>
        <div className="w-44">
          <Select
            aria-label="Zakres czasu"
            value={zakres}
            onChange={(e) => setZakres(e.target.value)}
            options={ZAKRESY.map((z) => ({ value: z.wartosc, label: z.etykieta }))}
          />
        </div>
        <button onClick={() => void odswiez()} className="iconbtn ml-auto" title="Odśwież">
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      <div className="card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('printing.colTime', 'Czas')}</TableHead>
              <TableHead>{t('printing.colDoc', 'Dokument')}</TableHead>
              <TableHead>{t('printing.colPrinter', 'Drukarka')}</TableHead>
              <TableHead>{t('printing.colAgent', 'Serwer')}</TableHead>
              <TableHead>{t('printing.colStatus', 'Status')}</TableHead>
              <TableHead>{t('printing.colAttempts', 'Próby')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableLoading colSpan={kolumn} rows={5} />
            ) : zadania.length === 0 ? (
              <TableEmpty
                colSpan={kolumn}
                icon={<ListTodo className="w-10 h-10" />}
                message={t('printing.noJobs', 'Brak zadań druku w wybranym zakresie')}
                description={t(
                  'printing.noJobsHint',
                  'Zadania pojawią się tu, gdy moduły zaczną drukować albo zlecisz wydruk testowy.'
                )}
              />
            ) : (
              zadania.map((zadanie) => {
                const opis = opiszZadanie(zadanie);
                return (
                  <TableRow
                    key={zadanie.id}
                    className="cursor-pointer"
                    onClick={() => setWybrane(zadanie)}
                  >
                    <TableCell className="mono text-xs whitespace-nowrap">
                      {formatujDate(zadanie.created_at)}
                    </TableCell>
                    <TableCell className="font-medium text-aurora-text">
                      {zadanie.document_type}
                      <span className="ml-2 text-xs text-aurora-subtle">{zadanie.format}</span>
                    </TableCell>
                    <TableCell>{nazwaDrukarki(zadanie.printer_id)}</TableCell>
                    <TableCell>{nazwaSerwera(zadanie.claimed_by)}</TableCell>
                    <TableCell>
                      <Badge variant={opis.wariant} size="sm">
                        {opis.symbol} {opis.etykieta}
                      </Badge>
                      {zadanie.status === 'queued' && (
                        <span className="ml-2 text-xs text-aurora-subtle">
                          od {formatujCzasWzgledny(zadanie.created_at)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="mono text-xs">{zadanie.attempts}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {wybrane && (
        <SzczegolyZadania
          zadanie={wybrane}
          nazwaDrukarki={nazwaDrukarki}
          nazwaSerwera={nazwaSerwera}
          zapisMozliwy={zapisMozliwy}
          onZamknij={zamknijDrawer}
          onPonow={() => void ponow(wybrane)}
          onAnuluj={() => void anuluj(wybrane)}
        />
      )}
    </div>
  );
}

// ── Drawer szczegółów zadania ────────────────────────────────────────────────

interface SzczegolyProps {
  zadanie: ZadanieWiersz;
  nazwaDrukarki: (id: string | null) => string;
  nazwaSerwera: (id: string | null) => string;
  zapisMozliwy: boolean;
  onZamknij: () => void;
  onPonow: () => void;
  onAnuluj: () => void;
}

function SzczegolyZadania({
  zadanie,
  nazwaDrukarki,
  nazwaSerwera,
  zapisMozliwy,
  onZamknij,
  onPonow,
  onAnuluj,
}: SzczegolyProps) {
  const { t } = useTranslation();
  const opis = opiszZadanie(zadanie);

  const osCzasu: Array<{ etykieta: string; kiedy: string | null }> = [
    { etykieta: 'utworzone', kiedy: zadanie.created_at },
    { etykieta: 'przejęte przez serwer', kiedy: zadanie.picked_at },
    { etykieta: 'wydrukowane', kiedy: zadanie.printed_at },
    { etykieta: 'zamknięte', kiedy: zadanie.done_at },
  ];

  const slad = zadanie.slad ?? {};
  const wpisySladu = Object.entries(slad).filter(([, v]) => typeof v === 'string' && v);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onZamknij} />
      <aside className="relative w-full max-w-md h-full bg-aurora-surface border-l border-aurora-border shadow-elev-4 overflow-y-auto p-6 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="display text-lg font-semibold text-aurora-text">
              {zadanie.document_type}
            </h2>
            <p className="mono text-xs text-aurora-subtle break-all">{zadanie.id}</p>
          </div>
          <button onClick={onZamknij} className="iconbtn" aria-label="Zamknij szczegóły">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant={opis.wariant}>
            {opis.symbol} {opis.etykieta}
          </Badge>
          {opis.porzucone && (
            <span className="text-xs text-red-400">
              {t('printing.deadLetterHint', 'serwer druku padł w trakcie — zadanie porzucone')}
            </span>
          )}
        </div>

        {/* Oś czasu z prawdziwych znaczników — brak znacznika = krok nie zaszedł. */}
        <div>
          <h3 className="overline mb-2">{t('printing.timeline', 'Oś czasu')}</h3>
          <ol className="space-y-1">
            {osCzasu.map((krok) => (
              <li key={krok.etykieta} className="flex items-center gap-2 text-sm">
                <span className={krok.kiedy ? 'text-green-400' : 'text-aurora-subtle'} aria-hidden>
                  {krok.kiedy ? '●' : '○'}
                </span>
                <span className={krok.kiedy ? 'text-aurora-text' : 'text-aurora-subtle'}>
                  {krok.etykieta}
                </span>
                <span className="ml-auto mono text-xs text-aurora-muted">
                  {krok.kiedy ? formatujDate(krok.kiedy) : '—'}
                </span>
              </li>
            ))}
          </ol>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-aurora-subtle text-xs">{t('printing.colPrinter', 'Drukarka')}</p>
            <p className="text-aurora-muted">{nazwaDrukarki(zadanie.printer_id)}</p>
          </div>
          <div>
            <p className="text-aurora-subtle text-xs">{t('printing.colAgent', 'Serwer')}</p>
            <p className="text-aurora-muted">{nazwaSerwera(zadanie.claimed_by)}</p>
          </div>
          <div>
            <p className="text-aurora-subtle text-xs">Format</p>
            <p className="text-aurora-muted mono text-xs">{zadanie.format}</p>
          </div>
          <div>
            <p className="text-aurora-subtle text-xs">{t('printing.copies', 'Kopie')}</p>
            <p className="text-aurora-muted">{zadanie.copies}</p>
          </div>
          <div>
            <p className="text-aurora-subtle text-xs">{t('printing.attempts', 'Próby')}</p>
            <p className="text-aurora-muted">{zadanie.attempts}</p>
          </div>
          <div>
            <p className="text-aurora-subtle text-xs">{t('printing.createdBy', 'Zlecił')}</p>
            <p className="text-aurora-muted mono text-xs break-all">{zadanie.created_by ?? '—'}</p>
          </div>
        </div>

        {/* Bajty wydruku celowo nie są pobierane do panelu — pokazujemy tylko
            metadane. To monitor, nie podgląd treści. */}
        <p className="text-xs text-aurora-subtle">
          {t('printing.payloadNote', 'Treść wydruku nie jest pobierana do panelu.')}
        </p>

        {zadanie.error && (
          <div>
            <h3 className="overline mb-2">{t('printing.errorFull', 'Pełny błąd')}</h3>
            <pre className="p-3 bg-red-500/10 border border-red-500/20 rounded-ids-md text-xs text-red-400 whitespace-pre-wrap break-all">
              {zadanie.error}
            </pre>
          </div>
        )}

        {wpisySladu.length > 0 && (
          <div>
            <h3 className="overline mb-2">{t('printing.trace', 'Ślad produkcyjny')}</h3>
            <div className="flex flex-wrap gap-2">
              {wpisySladu.map(([klucz, wartosc]) => (
                <span key={klucz} className="badge mono text-xs" title={String(wartosc)}>
                  {klucz}: {String(wartosc).slice(0, 8)}…
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-3 pt-2 border-t border-aurora-border">
          {(zadanie.status === 'done' || zadanie.status === 'error') && (
            <Button
              onClick={onPonow}
              disabled={!zapisMozliwy}
              title={zapisMozliwy ? undefined : KOMUNIKAT_BRAK_PRINT_CORE}
            >
              {t('printing.reprint', 'Ponów')}
            </Button>
          )}
          {/* Anuluj istnieje TYLKO dla `queued` — nie „disabled na zawsze". */}
          {zadanie.status === 'queued' && (
            <Button
              variant="danger"
              onClick={onAnuluj}
              disabled={!zapisMozliwy}
              title={zapisMozliwy ? undefined : KOMUNIKAT_BRAK_PRINT_CORE}
            >
              {t('printing.cancelJob', 'Anuluj zadanie')}
            </Button>
          )}
          {(zadanie.status === 'picked' || zadanie.status === 'printing') && (
            <p className="text-xs text-aurora-subtle self-center">
              {t(
                'printing.cannotCancel',
                'Zadanie przejęte przez serwer druku — nie można go anulować.'
              )}
            </p>
          )}
        </div>
      </aside>
    </div>
  );
}
